import { useEffect } from 'react';

interface Props {
  /** How many accusations would be thrown away. */
  count: number;
  onLeave: () => void;
  onStay: () => void;
}

/**
 * "You have work in progress — really leave?"
 *
 * Only shown when there is something to lose. Leaving a case you have not
 * touched is not a decision worth interrupting, and a confirm that fires every
 * time teaches people to dismiss it without reading.
 *
 * Reuses the detail sheet's scrim and panel so it behaves like the rest of the
 * app on a phone: anchored to the bottom, thumb-reachable, dismissed by tapping
 * away from it.
 */
export function ConfirmLeave({ count, onLeave, onStay }: Props) {
  // Escape is the other obvious way to dismiss this, and a dialog that traps
  // the key is worse than no dialog.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onStay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onStay]);

  return (
    <div
      className="scrim"
      onClick={onStay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-leave-title"
    >
      {/* Stops a tap inside the sheet counting as a tap on the scrim. */}
      <div className="sheet" onClick={(event) => event.stopPropagation()}>
        <div className="sheet__grip" aria-hidden="true" />
        <div className="sheet__head">
          <h2 className="sheet__title" id="confirm-leave-title">
            Leave this case?
          </h2>
        </div>

        <p className="confirm__body">
          You have accused {count} {count === 1 ? 'arrow' : 'arrows'}. Leaving now
          throws that away — the case is not scored and nothing is recorded.
        </p>

        <div className="sheet__actions">
          <button type="button" className="btn" onClick={onStay}>
            Keep playing
          </button>
          <button type="button" className="btn btn--ghost" onClick={onLeave}>
            Leave the case
          </button>
        </div>
      </div>
    </div>
  );
}
